import React from 'react';
import StandardButton from '@atlaskit/button/standard-button';
import { DropdownItemGroup as DropMenuItemGroup } from '@atlaskit/dropdown-menu';
import AddIcon from '@atlaskit/icon/glyph/editor/add';
import FilterIcon from '@atlaskit/icon/glyph/filter';
import MoreIcon from '@atlaskit/icon/glyph/more';
import RoadmapIcon from '@atlaskit/icon/glyph/roadmap';
import Tooltip from '@atlaskit/tooltip';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl';
import DropMenu, { DropMenuItem } from '@atlassian/jira-portfolio-3-common/src/drop-menu/index.tsx';
import {
	getPlanId,
	getScenarioId,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/util/urls.tsx';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import commonDependencyMessages from '../../messages.tsx';
import messages from './messages.tsx';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types.tsx';

export type HocProps = { intl: IntlShape };

function IssueDropMenu({
	defaultOpen,
	intl,
	issue,
	locateIssueInRoadMap,
	onFilterByIssue,
	rolledUpBy,
	setIssueFilter,
	showAddDependency,
	isEditMode,
}: Props & HocProps) {
	const isRolledUp = isDefined(rolledUpBy) && rolledUpBy !== 'none';
	const { push } = useRouterActions();
	const planId = getPlanId();
	const scenarioId = getScenarioId();

	return (
		<DropMenu
			defaultOpen={defaultOpen}
			trigger={({ triggerRef, ...props }) => (
				<StandardButton
					appearance="subtle"
					iconAfter={<span />}
					spacing="none"
					{...props}
					ref={triggerRef}
					aria-label={intl.formatMessage(messages.meatballsMenuAriaLabel)}
				>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.moreIconWrapper}>
						<MoreIcon label="" />
					</div>
				</StandardButton>
			)}
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			onOpenChange={() => {}}
			placement="bottom-end"
			testId={`portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.issue.meatballs-menu-issue-${issue.id}`}
		>
			<DropMenuItemGroup>
				{isEditMode && (
					<div
						data-testid={`portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.issue.meatballs-menu.add-dependency-${issue.id}`}
					>
						<DropMenuItem
							elemBefore={<AddIcon size="medium" label="" />}
							onClick={() => {
								showAddDependency(issue.id);
							}}
						>
							{intl.formatMessage(commonDependencyMessages.addDependency)}
						</DropMenuItem>
					</div>
				)}
				<>
					<div
						data-testid={`portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.issue.meatballs-menu.filter-by-issue-${issue.id}`}
					>
						<DropMenuItem
							isDisabled={isRolledUp}
							onClick={() => {
								setIssueFilter(issue.id);
								onFilterByIssue(issue.id);
							}}
							elemBefore={<FilterIcon size="medium" label="" />}
						>
							{isRolledUp ? (
								<Tooltip content={intl.formatMessage(messages.issueFilterWithRollupNotSupported)}>
									<span>{intl.formatMessage(messages.filterByThisIssue)}</span>
								</Tooltip>
							) : (
								intl.formatMessage(messages.filterByThisIssue)
							)}
						</DropMenuItem>
					</div>
					{isEditMode && (
						<div
							data-testid={`portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.issue.meatballs-menu.find-issue-${issue.id}-in-roadmap`}
						>
							<DropMenuItem
								onClick={() => {
									if (planId && scenarioId) {
										push(`/jira/plans/${planId}/scenarios/${scenarioId}/timeline`);
									}
									locateIssueInRoadMap(issue.summary);
								}}
								elemBefore={<RoadmapIcon size="medium" label="" />}
							>
								{intl.formatMessage(messages.locateIssueInTimeline)}
							</DropMenuItem>
						</div>
					)}
				</>
			</DropMenuItemGroup>
		</DropMenu>
	);
}

export default injectIntl(IssueDropMenu);
